// Nutrition Laddoo DISPLAY expansion (display-only; mirrors backend laddoo_display.py).
// The calc engine emits a single "Laddoo" line (1 no @ cost_per_piece). We expand it
// into its ingredient breakdown. The laddoo subtotal always equals the engine's laddoo
// cost (students * cost); the making-charge absorbs rounding so totals stay identical.
import { getConfig } from "./calcEngine";

export const MAIN_ORDER = ["Rice", "Dal", "Soya", "Egg", "Vegetables", "Oil", "Condiment & Salt", "Fuel"];

const ITEM_LABELS = {
  Rice: { en: "Rice", od: "ଚାଉଳ" },
  Dal: { en: "Dal", od: "ଡାଲି" },
  Soya: { en: "Soya", od: "ସୋୟା" },
  Egg: { en: "Egg", od: "ଅଣ୍ଡା" },
  Vegetables: { en: "Vegetables", od: "ପରିବା" },
  Oil: { en: "Oil", od: "ତେଲ" },
  "Condiment & Salt": { en: "Masala & Salt", od: "ମସଲା ଓ ଲୁଣ" },
  Fuel: { en: "Fuel", od: "ଇନ୍ଧନ" },
};

// Quantities only. Ingredient rates are never shipped to the browser (admin gets them from the API).
export const BASE_RECIPE = [
  { key: "ragi", name: "Ragi flour", name_od: "ମାଣ୍ଡିଆ ଅଟା", emoji: "🌾", grams: 12 },
  { key: "wheat", name: "Wheat flour", name_od: "ଗହମ ଅଟା", emoji: "🌾", grams: 12 },
  { key: "guda", name: "Guda / Sugar", name_od: "ଗୁଡ଼ / ଚିନି", emoji: "🍬", grams: 6 },
  { key: "sesame", name: "Sesame seeds", name_od: "ରାଶି", emoji: "⚪", grams: 1 },
  { key: "groundnut", name: "Groundnuts", name_od: "ଚିନାବାଦାମ", emoji: "🥜", grams: 1 },
  { key: "ghee", name: "Ghee", name_od: "ଘିଅ", emoji: "🧈", grams: 2 },
  { key: "cardamom", name: "Green cardamom powder", name_od: "ଅଳେଇଚ ଗୁଣ୍ଡ", emoji: "🌿", grams: 0.04 },
  { key: "making", name: "Laddoo making charge", name_od: "ଲଡୁ ପ୍ରସ୍ତୁତି ଖର୍ଚ୍ଚ", emoji: "👨‍🍳", grams: null },
];

export function itemLabel(name, lang) {
  const m = ITEM_LABELS[name];
  if (!m) return name;
  return lang === "or" ? m.od : m.en;
}

export function laddooIngLabel(ing, lang) {
  return lang === "or" ? (ing.name_od || ing.name) : ing.name;
}

function roundJs(x) { return Math.floor(x * 100 + 0.5) / 100; }

export function recipeTotal(recipe) {
  return roundJs(recipe.reduce((s, i) => s + Number(i.rate || 0), 0));
}

export function getLaddooRecipe() {
  const cfg = getConfig();
  const ings = cfg && cfg.laddoo_recipe && cfg.laddoo_recipe.ingredients;
  return (ings && ings.length) ? ings : BASE_RECIPE;
}

export function scaleRecipe(recipe, cost) {
  const baseTotal = recipeTotal(recipe);
  if (Math.abs(cost - baseTotal) < 0.005) return { ings: recipe, estimated: false };
  const factor = baseTotal ? cost / baseTotal : 1;
  let sumNon = 0;
  const scaled = [];
  for (const ing of recipe) {
    if (ing.key === "making") { scaled.push({ ...ing }); }
    else { const r = roundJs(Number(ing.rate) * factor); sumNon += r; scaled.push({ ...ing, rate: r }); }
  }
  for (const s of scaled) if (s.key === "making") s.rate = roundJs(cost - sumNon);
  return { ings: scaled, estimated: true };
}

export function expandLaddoo(students, cost, recipe) {
  if (cost == null || !recipe.some((i) => i.rate != null)) {
    const rows = recipe.map((ing) => ({ key: ing.key, name: ing.name, name_od: ing.name_od, emoji: ing.emoji,
      gramsPerLaddoo: ing.grams, totalG: ing.grams != null ? ing.grams * students : null }));
    return { rows, subtotal: null, estimated: false, students, qtyOnly: true };
  }
  const { ings, estimated } = scaleRecipe(recipe, cost);
  const subtotal = roundJs(cost * students);
  const rows = [];
  let sumOthers = 0;
  for (const ing of ings) {
    if (ing.key === "making") continue;
    const amt = roundJs(Number(ing.rate) * students);
    sumOthers += amt;
    rows.push({
      key: ing.key, name: ing.name, name_od: ing.name_od, emoji: ing.emoji,
      gramsPerLaddoo: ing.grams, totalG: ing.grams != null ? ing.grams * students : null,
      ratePerLaddoo: Number(ing.rate), amount: amt,
    });
  }
  const mk = ings.find((i) => i.key === "making");
  rows.push({
    key: "making", name: mk.name, name_od: mk.name_od, emoji: mk.emoji,
    gramsPerLaddoo: null, totalG: null, ratePerLaddoo: Number(mk.rate),
    amount: roundJs(subtotal - sumOthers),
  });
  return { rows, subtotal, estimated, students };
}

export function ladQtyPerLaddoo(g) {
  if (g == null) return "—";
  return String(g) + " g";
}

export function ladQtyTotal(g) {
  if (g == null) return "—";
  if (g >= 1000) return (g / 1000).toFixed(2) + " kg";
  return (Math.abs(g - Math.round(g)) < 1e-9 ? String(Math.round(g)) : g.toFixed(2)) + " g";
}

export function orderedItems(items) {
  const out = [];
  for (const n of MAIN_ORDER) if (items[n]) out.push([n, items[n]]);
  for (const n of Object.keys(items)) if (!MAIN_ORDER.includes(n) && n !== "Laddoo") out.push([n, items[n]]);
  return out;
}
